import React, { useState, useEffect } from 'react';
import { Button, FormControl, Alert, Modal } from 'react-bootstrap';
import {
    SliderContainer,
    SliderRow,
    SliderLabel,
    SliderValue,
    SliderInput,
    TotalRow,
    TotalLabel,
    TotalValue,
    CenteredModal
} from './styled-components';
import { IEditDistributionModalProps, IVendorDistribution } from './types';
import { adjustDistributionValues, calculateTotal } from './utils/distribution-helpers';

const EditDistributionModal: React.FC<IEditDistributionModalProps> = ({
    show,
    entityName,
    distributions,
    onSave,
    onCancel
}) => {
    const [values, setValues] = useState<IVendorDistribution[]>([]);
    const [isValid, setIsValid] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (show) {
            setValues([...distributions]);
            setIsValid(true);
            setErrorMessage(null);
        }
    }, [show, distributions]);

    const total = calculateTotal(values);

    const handleSliderChange = (index: number, newValue: number) => {
        let result = adjustDistributionValues(values, index, newValue);

        setValues(result.updatedValues);
        setIsValid(result.isValid);

        if (result.errorMessage) {
            setErrorMessage(result.errorMessage);
        }
    };

    // Handle direct input change
    const handleInputChange = (index: number, value: string) => {
        let numValue = parseInt(value, 10);

        if (isNaN(numValue)) {
            return;
        }

        let newValue = Math.max(0, Math.min(100, numValue));
        let result = adjustDistributionValues(values, index, newValue);

        setValues(result.updatedValues);
        setIsValid(result.isValid);
        setErrorMessage(result.errorMessage);
    };

    const handleSave = () => {
        if (isValid) {
            onSave(values);
        }
    };

    return (
        <CenteredModal show={show} backdrop='static' onHide={onCancel}>
            <Modal.Header closeButton>
                <Modal.Title>
                    Edit Distribution for {entityName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    Adjust the sliders to set the distribution percentage for each vendor.
                    The total must equal 100%.
                </p>

                {errorMessage && (
                    <Alert bsStyle='danger'>{errorMessage}</Alert>
                )}

                {values.map((item, index) => (
                    <SliderContainer key={item.id || index}>
                        <SliderRow>
                            <SliderLabel>{item.name}</SliderLabel>
                            <SliderInput
                                type='range'
                                min='0'
                                max='100'
                                value={item.odds}
                                onChange={(e) => handleSliderChange(index, parseInt(e.target.value, 10))}
                            />
                            <SliderValue>
                                <FormControl
                                    type='number'
                                    min='0'
                                    max='100'
                                    value={item.odds}
                                    onChange={(e) => handleInputChange(index, (e.target as HTMLInputElement).value)}
                                    style={{ width: '60px' }}
                                />
                            </SliderValue>
                        </SliderRow>
                    </SliderContainer>
                ))}

                <TotalRow>
                    <TotalLabel>Total</TotalLabel>
                    <div style={{ flex: 1 }}></div>
                    <TotalValue isValid={isValid}>
                        {total}% {isValid ? '✓' : '✗'}
                    </TotalValue>
                </TotalRow>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onCancel}>Cancel</Button>
                <Button bsStyle='primary' onClick={handleSave} disabled={!isValid}>Save Changes</Button>
            </Modal.Footer>
        </CenteredModal>
    );
};

export default EditDistributionModal;